import {isBefore} from 'date-fns';
import {z} from 'zod';

import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

const requiredMessage = () => i18n.t('general.validations.fieldIsRequired');

const MIN_ODOMETER_KM = 0;

// Mirrors the backend validity guard (VehicleWarrantyProduct::checkValidity) so a bad row is
// caught in the dialog instead of after the vehicle has already been created. All bounds are
// inclusive: equal dates and equal odometer values are allowed.
export const getVehicleWarrantyRowFormSchema = (existingProductIds: string[]) =>
  z
    .object({
      warrantyProductId: z
        .string({required_error: requiredMessage(), invalid_type_error: requiredMessage()})
        .min(1, requiredMessage())
        .refine((warrantyProductId) => !existingProductIds.includes(warrantyProductId), {
          message: i18n.t('entity.vehicleWarranty.validations.warrantyProductAlreadyAssigned', {
            defaultValue: 'This warranty type is already assigned to the vehicle',
          }),
        }),
      startDate: z.date({required_error: requiredMessage(), invalid_type_error: requiredMessage()}),
      endDate: z.date().nullable(),
      startOdometerKm: z
        .number()
        .min(MIN_ODOMETER_KM, i18n.t('general.validations.minValue', {min: MIN_ODOMETER_KM}))
        .nullable(),
      endOdometerKm: z
        .number()
        .min(MIN_ODOMETER_KM, i18n.t('general.validations.minValue', {min: MIN_ODOMETER_KM}))
        .nullable(),
    })
    .superRefine((row, context) => {
      if (isNotNil(row.endDate) && isBefore(row.endDate, row.startDate)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endDate'],
          message: i18n.t('general.validations.startDateMustBeBeforeEndDate'),
        });
      }

      if (
        isNotNil(row.startOdometerKm) &&
        isNotNil(row.endOdometerKm) &&
        row.endOdometerKm < row.startOdometerKm
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endOdometerKm'],
          message: i18n.t('entity.vehicleWarranty.validations.endOdometerBelowStartOdometer', {
            defaultValue: 'End odometer must not be lower than start odometer',
          }),
        });
      }
    });

export type VehicleWarrantyRowFormType = z.infer<
  ReturnType<typeof getVehicleWarrantyRowFormSchema>
>;
